import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import * as amplitude from '@amplitude/analytics-browser';
import { toJS } from 'mobx';
import { cast, getParent, types } from 'mobx-state-tree';
import { api } from 'src/api';
import { createAsync } from 'src/utils/asyncModel';
import { restoreSelectedCompany } from 'src/lib/sessionCompany';
import { AUTH_TOKEN_KEYS } from 'src/constants/authToken';
import { USER_ROLES_BY_NAME, USER_SETTINGS_KEYS, isPmHolderOrHigher, isPmRegularOrHigher } from 'src/constants/userSetting';
import { parseJwt } from 'src/core/services/jwt';
import { singleElementOfArray } from 'src/utils/singleElementOfArray';
const registerNewAccountAsync = createAsync(body => {
  return async function registerNewAccount(options) {
    const resp = await api.Account.registerNewAccount(body, options);
    getParent(this).auth.setTokens(resp.response);
    localStorage.setItem(AUTH_TOKEN_KEYS.accessToken, resp.response.accessToken);
    localStorage.setItem(AUTH_TOKEN_KEYS.expiresOn, resp.response.expiresOn);
    localStorage.setItem(AUTH_TOKEN_KEYS.refreshToken, resp.response.refreshToken);
    localStorage.setItem(AUTH_TOKEN_KEYS.requireMfa, resp.response.requireMfa);
    localStorage.setItem(AUTH_TOKEN_KEYS.session, resp.response.session);
    localStorage.setItem(USER_SETTINGS_KEYS.company, 'null');
    return resp;
  };
});
const userProfileInitialData = {
  id: '',
  username: '',
  firstName: '',
  lastName: '',
  phone: null,
  email: '',
  userRole: USER_ROLES_BY_NAME.NonSpecified,
  emailConfirmed: false,
  phoneConfirmed: false,
  mfaEnabled: false,
  mfaMethods: [],
  preferredMfaMethod: null
};

/**
 * The signed-in role, mirror first and token second.
 *
 * The mirror is only written once getProfile resolves. A gate that reads it alone is therefore
 * racing an unrelated request: ReportingPage releases on the COMPANY fetch, so whenever that
 * returns first the mirror is still empty, a property manager reads as no role at all, and the
 * page redirects them to the dashboard with no way back until they navigate again. The token
 * carries custom:role before either request is made, which is what closes the race.
 */
export const currentRole = () => {
  var _parseJwt;
  return localStorage.getItem(USER_SETTINGS_KEYS.userRole) || ((_parseJwt = parseJwt(localStorage.getItem(AUTH_TOKEN_KEYS.accessToken))) === null || _parseJwt === void 0 ? void 0 : _parseJwt['custom:role']) || '';
};

/**
 * Livable staff sign in with no company on the token. The company lookup that follows a login is
 * meaningless for them: it returns nothing and leaves 'null' in storage, which the route guard reads
 * as an unfinished PM onboarding.
 */
export const isStaffWithoutCompany = tokenData => {
  const companyId = tokenData === null || tokenData === void 0 ? void 0 : tokenData['custom:companyid'];
  const role = tokenData === null || tokenData === void 0 ? void 0 : tokenData['custom:role'];
  return (!companyId || companyId === 'null') && (role === USER_ROLES_BY_NAME.LivableAdmin || role === USER_ROLES_BY_NAME.LivableSuperAdmin);
};
const NULLABLE_PROFILE_FIELDS = ['phone', 'preferredMfaMethod'];
const userProfile = types.model('userProfile', {
  authIsConfirmedValue: types.optional(types.boolean, false),
  codeIsSentValue: types.optional(types.boolean, false),
  firstName: types.string,
  email: types.string,
  emailConfirmed: types.boolean,
  id: types.string,
  lastName: types.string,
  mfaEnabled: types.optional(types.boolean, false),
  mfaMethods: types.optional(types.array(types.string), []),
  preferredMfaMethod: types.maybeNull(types.string),
  phone: types.maybeNull(types.string),
  phoneConfirmed: types.boolean,
  username: types.string,
  userRole: types.string
}).actions(store => ({
  setAuthIsConfirmed(value) {
    try {
      store.authIsConfirmedValue = value;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setMfaEnabled(value) {
    try {
      store.mfaEnabled = value;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setMfaMethods(methods) {
    try {
      var _nextMethods$;
      const nextMethods = methods !== null && methods !== void 0 ? methods : [];
      store.mfaMethods.replace(nextMethods);
      store.mfaEnabled = nextMethods.length > 0;
      // Mirrors the backend rule: the authenticator app is the preferred challenge whenever it is on.
      store.preferredMfaMethod = nextMethods.includes('TOTP') ? 'TOTP' : (_nextMethods$ = nextMethods[0]) !== null && _nextMethods$ !== void 0 ? _nextMethods$ : null;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setCodeIsSent(value) {
    try {
      store.codeIsSentValue = value;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setUserProfile(data) {
    try {
      Object.keys(data).forEach(key => {
        if (!(key in store)) return;
        const value = data[key];

        // `phone` and `preferredMfaMethod` are the nullable string fields; every other string field
        // in the model is required, so coerce null/undefined to '' to avoid
        // MST "value `null` is not assignable to type `string`" errors.
        if (value == null && !NULLABLE_PROFILE_FIELDS.includes(key)) {
          if (typeof store[key] === 'string') store[key] = '';
          return;
        }
        store[key] = value;
      });
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  updatePhone(phone) {
    try {
      store.phone = phone;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  }
})).views(store => ({
  get authIsConfirmed() {
    return store.authIsConfirmedValue;
  },
  get codeIsSent() {
    return store.codeIsSentValue;
  },
  get isUserSuperAdmin() {
    return USER_ROLES_BY_NAME[localStorage.getItem(USER_SETTINGS_KEYS.userRole)] === USER_ROLES_BY_NAME.LivableSuperAdmin;
  },
  get isUserAdmin() {
    return USER_ROLES_BY_NAME[localStorage.getItem(USER_SETTINGS_KEYS.userRole)] === USER_ROLES_BY_NAME.LivableAdmin;
  },
  // Livable staff = super admin OR admin. Matches the API's Permissions.AdminOrHigher, so the UI
  // and the endpoint agree on who may see a staff-only feature. Gating on isUserSuperAdmin alone
  // left exactly one prod account able to use anything behind it.
  get isUserLivableStaff() {
    var _localStorage$getItem;
    // `?? ''` where the neighbours index with a possibly-null key: same behaviour, minus a new
    // TS2538 in a file that already carries several.
    const role = USER_ROLES_BY_NAME[(_localStorage$getItem = localStorage.getItem(USER_SETTINGS_KEYS.userRole)) !== null && _localStorage$getItem !== void 0 ? _localStorage$getItem : ''];
    return role === USER_ROLES_BY_NAME.LivableSuperAdmin || role === USER_ROLES_BY_NAME.LivableAdmin;
  },
  get isUserPropertyManagerAccountHolder() {
    return USER_ROLES_BY_NAME[localStorage.getItem(USER_SETTINGS_KEYS.userRole)] === USER_ROLES_BY_NAME.PropertyManagerAccountHolder;
  },
  // Livable staff + the PM account holder, i.e. the API's Permissions.PmHolderOrHigher.
  get isUserPmHolderOrHigher() {
    return isPmHolderOrHigher(currentRole());
  },
  // Livable staff + both PM roles, i.e. the API's Permissions.PmRegularOrHigher. Gate any surface
  // whose endpoints carry that policy on this, so the UI and the API agree on who gets in.
  get isUserPmRegularOrHigher() {
    return isPmRegularOrHigher(currentRole());
  },
  get isUserHasAccessToThePayment() {
    return isPmRegularOrHigher(currentRole());
  },
  get isUserPropertyManagerRegular() {
    return USER_ROLES_BY_NAME[localStorage.getItem(USER_SETTINGS_KEYS.userRole)] === USER_ROLES_BY_NAME.PropertyManagerRegular || store.userRole === USER_ROLES_BY_NAME.PropertyManagerRegular;
  },
  get isDemoModeEnabled() {
    return localStorage.getItem(AUTH_TOKEN_KEYS.demoPropertyMode) === 'Demo';
  }
}));
const getProfileAsync = createAsync(function () {
  let loadUserData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
  return async function getProfile(options) {
    if (loadUserData) {
      localStorage.setItem(USER_SETTINGS_KEYS.emailConfirmed, 'false');
      localStorage.setItem(USER_SETTINGS_KEYS.phoneConfirmed, 'false');
    }
    const resp = await api.Account.getProfile(options);
    this.userProfile.setUserProfile(resp.response);
    localStorage.setItem(USER_SETTINGS_KEYS.emailConfirmed, resp.response.emailConfirmed);
    localStorage.setItem(USER_SETTINGS_KEYS.phoneConfirmed, resp.response.phoneConfirmed);
    localStorage.setItem(USER_SETTINGS_KEYS.userRole, resp.response.userRole);
    return resp;
  };
});
const updateProfileAsync = createAsync(body => {
  return async function updateProfile(options) {
    const resp = await api.Account.updateProfile(body, options);
    return resp;
  };
});
const confirmAnAccountsEmailAsync = createAsync(body => {
  return async function confirmAnAccountsEmail(options) {
    const resp = await api.Account.confirmAnAccountsEmail(body, options);
    return resp;
  };
});
const refreshAccessTokenAsync = createAsync(function () {
  let companyAccessData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  return async function refreshAccessToken(options) {
    var _resp$response;
    if (companyAccessData !== null) {
      localStorage.removeItem(AUTH_TOKEN_KEYS.demoPropertyMode);
      const tokenData = parseJwt(companyAccessData === null || companyAccessData === void 0 ? void 0 : companyAccessData.accessToken);
      const demoPropertyMode = tokenData['custom:testmode'];
      if (demoPropertyMode) {
        localStorage.setItem(AUTH_TOKEN_KEYS.demoPropertyMode, demoPropertyMode);
      }
      getParent(this).auth.setTokensAfterRefresh(companyAccessData);
      localStorage.setItem(AUTH_TOKEN_KEYS.accessToken, companyAccessData.accessToken);
      localStorage.setItem(AUTH_TOKEN_KEYS.expiresOn, companyAccessData.expiresOn);
      window.dispatchEvent(new StorageEvent('triggerStorageTokenEventHandler'));
      return companyAccessData;
    }
    const resp = await api.Account.refreshAccessToken({
      refreshToken: localStorage.getItem(AUTH_TOKEN_KEYS.refreshToken)
    }, options);
    localStorage.removeItem(AUTH_TOKEN_KEYS.demoPropertyMode);
    const tokenData = parseJwt(resp === null || resp === void 0 || (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.accessToken);
    const demoPropertyMode = tokenData['custom:testmode'];
    if (demoPropertyMode) {
      localStorage.setItem(AUTH_TOKEN_KEYS.demoPropertyMode, demoPropertyMode);
    }
    getParent(this).auth.setTokensAfterRefresh(resp.response);
    localStorage.setItem(AUTH_TOKEN_KEYS.accessToken, resp.response.accessToken);
    localStorage.setItem(AUTH_TOKEN_KEYS.expiresOn, resp.response.expiresOn);
    window.dispatchEvent(new StorageEvent('triggerStorageTokenEventHandler'));
    // This refresh bypasses httpClient's, so keep the tab's selected company here too. A no-op
    // after a deliberate switch, whose impersonate call already recorded the new selection.
    await restoreSelectedCompany();
    return resp;
  };
});
const resendCodeToConfirmAccountEmailAsync = createAsync(body => {
  return async function resendCodeToConfirmAccountEmail(options) {
    const resp = await api.Account.resendCodeToConfirmAccountEmail(body, options);
    return resp;
  };
});
const loginInviteAsync = createAsync(body => {
  return async function loginInvite(options) {
    const resp = await api.Account.loginInvite(body, options);
    if (!resp.response.requireNewPassword) {
      getParent(this).auth.setTokens(resp.response);
      if (!resp.response.requireMfa) {
        const companyDetail = await api.Companies.getCompanies(options);
        if (companyDetail.response.length) {
          localStorage.setItem(USER_SETTINGS_KEYS.company, singleElementOfArray(companyDetail.response).key);
          getParent(this).homeDashboardStore.setCompanyAccessId(singleElementOfArray(companyDetail.response).key);
        } else {
          localStorage.setItem(USER_SETTINGS_KEYS.company, 'null');
        }
      }
      localStorage.setItem(AUTH_TOKEN_KEYS.accessToken, resp.response.accessToken);
      localStorage.setItem(AUTH_TOKEN_KEYS.expiresOn, resp.response.expiresOn);
      localStorage.setItem(AUTH_TOKEN_KEYS.refreshToken, resp.response.refreshToken);
      localStorage.setItem(AUTH_TOKEN_KEYS.requireMfa, resp.response.requireMfa);
      localStorage.setItem(AUTH_TOKEN_KEYS.session, resp.response.session);
    }
    return resp;
  };
});
const loginAccountAsync = createAsync(body => {
  return async function loginAccount(options) {
    const resp = await api.Account.login(body, options);
    if (!resp.response.requireNewPassword) {
      getParent(this).auth.setTokens(resp.response);
      if (!resp.response.requireMfa) {
        const companyDetail = await api.Companies.getCompanies(options);
        if (companyDetail.response.length) {
          localStorage.setItem(USER_SETTINGS_KEYS.company, singleElementOfArray(companyDetail.response).key);
          getParent(this).homeDashboardStore.setCompanyAccessId(singleElementOfArray(companyDetail.response).key);
        } else {
          localStorage.setItem(USER_SETTINGS_KEYS.company, 'null');
        }
      }
      localStorage.setItem(AUTH_TOKEN_KEYS.accessToken, resp.response.accessToken);
      localStorage.setItem(AUTH_TOKEN_KEYS.expiresOn, resp.response.expiresOn);
      localStorage.setItem(AUTH_TOKEN_KEYS.refreshToken, resp.response.refreshToken);
      localStorage.setItem(AUTH_TOKEN_KEYS.requireMfa, resp.response.requireMfa);
      localStorage.setItem(AUTH_TOKEN_KEYS.session, resp.response.session);
    }
    return resp;
  };
});
const loginAccountV2Async = createAsync(body => {
  return async function loginAccountV2(options) {
    const resp = await api.Account.loginPM(body, options);
    const tokenData = parseJwt(resp.response.accessToken);
    if (isStaffWithoutCompany(tokenData)) {
      localStorage.removeItem(USER_SETTINGS_KEYS.company);
      if (!resp.response.requireNewPassword) {
        getParent(this).auth.setTokens(resp.response);
        localStorage.setItem(AUTH_TOKEN_KEYS.accessToken, resp.response.accessToken);
        localStorage.setItem(AUTH_TOKEN_KEYS.expiresOn, resp.response.expiresOn);
        localStorage.setItem(AUTH_TOKEN_KEYS.refreshToken, resp.response.refreshToken);
        localStorage.setItem(AUTH_TOKEN_KEYS.requireMfa, resp.response.requireMfa);
        localStorage.setItem(AUTH_TOKEN_KEYS.session, resp.response.session);
      }
      await getParent(this).account.getProfileAsync.run();
    } else {
      if (!resp.response.requireNewPassword) {
        getParent(this).auth.setTokens(resp.response);
        if (!resp.response.requireMfa) {
          const companyDetail = await api.Companies.getCompanies(options);
          if (companyDetail.response.length) {
            localStorage.setItem(USER_SETTINGS_KEYS.company, singleElementOfArray(companyDetail.response).key);
            getParent(this).homeDashboardStore.setCompanyAccessId(singleElementOfArray(companyDetail.response).key);
          } else {
            localStorage.setItem(USER_SETTINGS_KEYS.company, 'null');
          }
        }
        localStorage.setItem(AUTH_TOKEN_KEYS.accessToken, resp.response.accessToken);
        localStorage.setItem(AUTH_TOKEN_KEYS.expiresOn, resp.response.expiresOn);
        localStorage.setItem(AUTH_TOKEN_KEYS.refreshToken, resp.response.refreshToken);
        localStorage.setItem(AUTH_TOKEN_KEYS.requireMfa, resp.response.requireMfa);
        localStorage.setItem(AUTH_TOKEN_KEYS.session, resp.response.session);
      }
    }
    if (tokenData && tokenData['custom:accountid']) amplitude.setUserId(tokenData['custom:accountid']);
    return resp;
  };
});
const mfaLoginAccountAsync = createAsync(body => {
  return async function mfaLoginAccount(options) {
    const resp = await api.Account.mfaLogin(body, options);
    getParent(this).auth.setTokens(resp.response);
    const staffWithoutCompany = isStaffWithoutCompany(parseJwt(resp.response.accessToken));
    if (staffWithoutCompany) {
      localStorage.removeItem(USER_SETTINGS_KEYS.company);
    } else {
      const companyDetail = await api.Companies.getCompanies(options);
      if (companyDetail.response.length) {
        localStorage.setItem(USER_SETTINGS_KEYS.company, singleElementOfArray(companyDetail.response).key);
        getParent(this).homeDashboardStore.setCompanyAccessId(singleElementOfArray(companyDetail.response).key);
      } else {
        localStorage.setItem(USER_SETTINGS_KEYS.company, 'null');
      }
    }
    localStorage.setItem(AUTH_TOKEN_KEYS.accessToken, resp.response.accessToken);
    localStorage.setItem(AUTH_TOKEN_KEYS.expiresOn, resp.response.expiresOn);
    localStorage.setItem(AUTH_TOKEN_KEYS.refreshToken, resp.response.refreshToken);
    localStorage.setItem(AUTH_TOKEN_KEYS.requireMfa, resp.response.requireMfa);
    localStorage.setItem(AUTH_TOKEN_KEYS.session, resp.response.session);
    if (staffWithoutCompany) {
      await getParent(this).account.getProfileAsync.run();
    }
    return resp;
  };
});
const logoutAccountAsync = createAsync(() => {
  return async function logoutAccount(options) {
    const data = {
      refreshToken: localStorage.getItem(AUTH_TOKEN_KEYS.refreshToken)
    };
    try {
      window.hsConversationsSettings = {};
      window.HubSpotConversations.resetAndReloadWidget();
    } catch (error) {}
    const resp = await api.Account.logout(data, options);
    getParent(this).auth.reset();
    getParent(this).payment.resetPaymentMethods();
    getParent(this).buildings.resetBuildingsIdsAfterLogout();
    getParent(this).notifications.resetPendingBanners();

    // Reset clears the Amplitude user id AND regenerates the device id, so the
    // next (anonymous) user's events don't attach to the previous session.
    amplitude.reset();
    return resp;
  };
});
const getUsersSubAccountsAsync = createAsync(() => {
  return async function getUsersSubAccounts(options) {
    const resp = await api.Account.getUsersSubAccounts(options);
    return resp;
  };
});
const postSwitchUsersSubAccountAsync = createAsync(body => {
  return async function postSwitchUsersSubAccount(options) {
    const resp = await api.Account.postSwitchUsersSubAccount(body, options);
    return resp;
  };
});
const forgotAccountPasswordAsync = createAsync(body => {
  return async function forgotAccountPassword(options) {
    const resp = await api.Account.forgotPassword(body, options);
    return resp;
  };
});
const resetAccountPasswordAsync = createAsync(body => {
  return async function resetAccountPassword(options) {
    const resp = await api.Account.resetPassword(body, options);
    return resp;
  };
});
const disableAccountMfaAsync = createAsync(body => {
  return async function disableAccountMfa(options) {
    const resp = await api.Account.disableMFA(body, options);
    return resp;
  };
});
const enableAccountMfaAsync = createAsync(body => {
  return async function enableAccountMfa(options) {
    const resp = await api.Account.enableMFA(body, options);
    return resp;
  };
});
const associateTotpAsync = createAsync(() => {
  return async function associateTotp(options) {
    const resp = await api.Account.associateTotp(options);
    return resp;
  };
});
const verifyTotpAsync = createAsync(body => {
  return async function verifyTotp(options) {
    const resp = await api.Account.verifyTotp(body, options);
    return resp;
  };
});
const updatePhoneNumberAsync = createAsync(body => {
  return async function updatePhoneNumber(options) {
    const resp = await api.Account.updatePhoneNumber(body, options);
    return resp;
  };
});
const confirmPhoneNumberAsync = createAsync(body => {
  return async function confirmPhoneNumber(options) {
    const resp = await api.Account.confirmPhoneNumber(body, options);
    return resp;
  };
});
const changeTemporaryPasswordAsync = createAsync(body => {
  return async function changeTemporaryPassword(options) {
    const resp = await api.Account.changeTemporaryPassword(body, options);
    return resp;
  };
});
const inviteNewUserAsync = createAsync(body => {
  return async function inviteNewUser(options) {
    const resp = await api.Account.inviteNewUser(body, options);
    return resp;
  };
});
const inviteNewUserV2Async = createAsync(body => {
  return async function inviteNewUserV2(options) {
    const resp = await api.Account.inviteNewUserV2(body, options);
    return resp;
  };
});
const reInviteUserAsync = createAsync(body => {
  return async function reInviteUser(options) {
    const resp = await api.Account.reInviteUser(body, options);
    return resp;
  };
});
const getUsersProfilesAsync = createAsync(body => {
  return async function getUsersProfiles(options) {
    const resp = await api.Account.getUsersProfiles(body, options);
    const _getParent = getParent(this),
      account = _getParent.account;
    account.setProfilesDataAfterFetch(resp, body.accountIds);
    return resp;
  };
});
const enrollUserAsync = createAsync(body => {
  return async function enrollUser(options) {
    const resp = await api.Account.enrollUser(body, options);
    return resp;
  };
});
const updateProfileByAccountIdAsync = createAsync(body => {
  return async function updateProfileByAccountId(options) {
    const resp = await api.Account.updateProfileByAccountId(body, options);
    return resp;
  };
});
const updatePhoneByAccountIdAsync = createAsync(body => {
  return async function updatePhoneByAccountId(options) {
    const resp = await api.Account.updatePhoneByAccountId(body, options);
    return resp;
  };
});
const updateUsersPortalAccessDateAsync = createAsync(body => {
  return async function updateUsersPortalAccessDate(options) {
    const resp = await api.Account.updateUsersPortalAccessDate(body, options);
    return resp;
  };
});
const updateEmailByAccountIdAsync = createAsync(body => {
  return async function updateEmailByAccountId(options) {
    const resp = await api.Account.updateEmailByAccountId(body, options);
    return resp;
  };
});
const setTeamMemberAccessAsync = createAsync(body => {
  return async function setTeamMemberAccess(options) {
    const resp = await api.Account.setTeamMemberAccess(body, options);
    return resp;
  };
});
const assignResourceTeamMemberAsync = createAsync(body => {
  return async function assignResourceTeamMember(options) {
    const resp = await api.Account.assignResourceTeamMember(body, options);
    return resp;
  };
});
const enableDemoPropertyAsync = createAsync(body => {
  return async function enableDemoProperty(options) {
    // Nothing here is logged: the response carries the new access token.
    return api.Account.enableDemoMode(body, options);
  };
});
const changeTemporaryInvitePasswordAsync = createAsync(body => {
  return async function changeTemporaryInvitePassword(options) {
    const resp = await api.Account.changeTemporaryInvitePassword(body, options);
    if (!resp.response.requireNewPassword) {
      getParent(this).auth.setTokens(resp.response);
      if (!resp.response.requireMfa) {
        const companyDetail = await api.Companies.getCompanies(options);
        if (companyDetail.response.length) {
          localStorage.setItem(USER_SETTINGS_KEYS.company, singleElementOfArray(companyDetail.response).key);
          getParent(this).homeDashboardStore.setCompanyAccessId(singleElementOfArray(companyDetail.response).key);
        } else {
          localStorage.setItem(USER_SETTINGS_KEYS.company, 'null');
        }
      }
      localStorage.setItem(AUTH_TOKEN_KEYS.accessToken, resp.response.accessToken);
      localStorage.setItem(AUTH_TOKEN_KEYS.expiresOn, resp.response.expiresOn);
      localStorage.setItem(AUTH_TOKEN_KEYS.refreshToken, resp.response.refreshToken);
      localStorage.setItem(AUTH_TOKEN_KEYS.requireMfa, resp.response.requireMfa);
      localStorage.setItem(AUTH_TOKEN_KEYS.session, resp.response.session);
    }
    return resp;
  };
});
const changeTemporaryInvitePasswordV2Async = createAsync(body => {
  return async function changeTemporaryInvitePasswordV2(options) {
    // A token link (LB-6162) sets the password in one call; an older link sends the session from logging
    // in with the temporary password it carries.
    const resp = body.token ? await api.Account.acceptInvite(body, options) : await api.Account.changeTemporaryInvitePassword(body, options);
    const tokenData = parseJwt(resp.response.accessToken);
    if (!resp.response.requireNewPassword) {
      getParent(this).auth.setTokens(resp.response);
      if ((!tokenData['custom:companyid'] || tokenData['custom:companyid'] === null || tokenData['custom:companyid'] === 'null') && (tokenData['custom:role'] === USER_ROLES_BY_NAME.LivableAdmin || tokenData['custom:role'] === USER_ROLES_BY_NAME.LivableSuperAdmin)) {
        localStorage.setItem(AUTH_TOKEN_KEYS.accessToken, resp.response.accessToken);
        localStorage.setItem(AUTH_TOKEN_KEYS.expiresOn, resp.response.expiresOn);
        localStorage.setItem(AUTH_TOKEN_KEYS.refreshToken, resp.response.refreshToken);
        localStorage.setItem(AUTH_TOKEN_KEYS.requireMfa, resp.response.requireMfa);
        localStorage.setItem(AUTH_TOKEN_KEYS.session, resp.response.session);
      } else {
        if (!resp.response.requireMfa) {
          const companyDetail = await api.Companies.getCompanies(options);
          if (companyDetail.response.length) {
            localStorage.setItem(USER_SETTINGS_KEYS.company, singleElementOfArray(companyDetail.response).key);
            getParent(this).homeDashboardStore.setCompanyAccessId(singleElementOfArray(companyDetail.response).key);
          } else {
            localStorage.setItem(USER_SETTINGS_KEYS.company, 'null');
          }
        }
        localStorage.setItem(AUTH_TOKEN_KEYS.accessToken, resp.response.accessToken);
        localStorage.setItem(AUTH_TOKEN_KEYS.expiresOn, resp.response.expiresOn);
        localStorage.setItem(AUTH_TOKEN_KEYS.refreshToken, resp.response.refreshToken);
        localStorage.setItem(AUTH_TOKEN_KEYS.requireMfa, resp.response.requireMfa);
        localStorage.setItem(AUTH_TOKEN_KEYS.session, resp.response.session);
      }
    }
    return resp;
  };
});
const mfaFlowStore = types.model('mfaFlowStore', {
  isEnabled: false,
  codeIsSent: false,
  smsSendRety: false,
  timeCounted: false,
  mfaIsActivated: false,
  mfaActivateFailed: false
}).actions(store => ({
  enableMFA() {
    store.isEnabled = true;
  },
  disabledMFA() {
    store.isEnabled = false;
  },
  toggleMFA() {
    store.isEnabled = !store.isEnabled;
  },
  codeIsSend() {
    store.codeIsSent = true;
    store.timeCounted = true;
  },
  timerIsFinished() {
    store.timeCounted = false;
  },
  activateMfa() {
    store.mfaIsActivated = true;
    store.mfaActivateFailed = false;
  },
  failedActivateMfa() {
    store.mfaIsActivated = false;
    store.mfaActivateFailed = false;
  }
}));
const featureFlagsStore = types.model('featureFlagsStore', {
  finalStatementEstimationEnabled: false,
  splitPaperFeeEnabled: false,
  ukrainianMobileCode: false,
  isAllocationCorrectionEnabled: false,
  isActivityLogsEnabled: false,
  isBillAutomationEnabled: false,
  isBillExtractionEnabled: false,
  isBillsDashboardEnabled: false,
  isAppfolioEnabled: false,
  isAppfolioTestEnabled: false,
  isMultiAccountAccessEnabled: false,
  isSubscriptionsAndPaymentsEnabled: false,
  isNewAllocationBillingEnabled: false,
  isSubmeterInterimSolutionEnabled: false,
  isPresignedFileFeatureEnabled: false,
  isBillingTemplatesEnabled: false,
  isNewPaymentsAndSubscriptions: false,
  isPushUtilityBillAppFolioEnabled: false,
  isNewLayoutBetaToggleEnabled: false,
  isNewPayoutEnabled: false,
  isAppfolioPaperStatementFeeEnabled: false,
  isAccountSetupFeeEnabled: false,
  isPmSubAccountsEnabled: false,
  isFailedInvoicePaymentEnabled: false,
  isStripeInvoicingApiEnabled: false,
  isConvergentBillingEnabled: false,
  isGlCodesEnabled: false,
  isCouponsEnabled: false,
  enableSubmeterBilling: false,
  // Off until set: an environment whose service does not keep notes yet never offers one (LB-6056).
  isBillNotesEnabled: false,
  // Off until set: utility setup's billing options (LB-6176) show only where the flag is on (LB-6177).
  isBillingOptionsEnabled: false
}).actions(store => ({
  setFeatureFlags(data) {
    Object.entries(data).forEach(_ref => {
      let _ref2 = _slicedToArray(_ref, 2),
        key = _ref2[0],
        value = _ref2[1];
      store["".concat(key)] = value;
    });
  }
})).views(store => ({
  get allocationCorrectionEnabled() {
    return store.isAllocationCorrectionEnabled;
  },
  get featureFlags() {
    return store;
  },
  get isFinalStatementEstimationEnabled() {
    return store.finalStatementEstimationEnabled;
  },
  get isSplitPaperFeeEnabled() {
    return store.splitPaperFeeEnabled;
  },
  get isUkrainianMobileCode() {
    return store.ukrainianMobileCode;
  },
  get getIsActivityLogsEnabled() {
    return store.isActivityLogsEnabled;
  },
  get getIsBillAutomationEnabled() {
    return store.isBillAutomationEnabled;
  },
  get getIsBillExtractionEnabled() {
    return store.isBillExtractionEnabled;
  },
  get getIsBillsDashboardEnabled() {
    return store.isBillsDashboardEnabled;
  },
  get getIsStripeInvoicingApiEnabled() {
    return store.isStripeInvoicingApiEnabled;
  },
  get getIsConvergentBillingEnabled() {
    return store.isConvergentBillingEnabled;
  },
  get getIsAppfolioEnabled() {
    return store.isAppfolioEnabled;
  },
  get getIsAppfolioTestEnabled() {
    return store.isAppfolioTestEnabled;
  },
  get getIsMultiAccountAccessEnabled() {
    return store.isMultiAccountAccessEnabled;
  },
  get getIsSubscriptionsAndPaymentsEnabled() {
    return store.isSubscriptionsAndPaymentsEnabled;
  },
  get getIsNewAllocationBillingEnabled() {
    return store.isNewAllocationBillingEnabled;
  },
  get getIsSubmeterInterimSolutionEnabled() {
    return store.isSubmeterInterimSolutionEnabled;
  },
  get getIsPresignedFileFeatureEnabled() {
    return store.isPresignedFileFeatureEnabled;
  },
  get getIsBillingTemplatesEnabled() {
    return store.isBillingTemplatesEnabled;
  },
  get getIsPushUtilityBillAppFolioEnabled() {
    return store.isPushUtilityBillAppFolioEnabled;
  },
  get getIsNewPaymentsAndSubscriptions() {
    return store.isNewPaymentsAndSubscriptions;
  },
  get getIsNewLayoutBetaToggleEnabled() {
    return store.isNewLayoutBetaToggleEnabled;
  },
  get getIsNewPayoutEnabled() {
    return store.isNewPayoutEnabled;
  },
  get getIsAppfolioPaperStatementFeeEnabled() {
    return store.isAppfolioPaperStatementFeeEnabled;
  },
  get getIsAccountSetupFeeEnabled() {
    return store.isAccountSetupFeeEnabled;
  },
  get getIsPmSubAccountsEnabled() {
    return store.isPmSubAccountsEnabled;
  },
  get getIsFailedInvoicePaymentEnabled() {
    return store.isFailedInvoicePaymentEnabled;
  },
  get getIsGlCodesEnabled() {
    return store.isGlCodesEnabled;
  },
  get getIsCouponsEnabled() {
    return store.isCouponsEnabled;
  },
  get getEnableSubmeterBilling() {
    return store.enableSubmeterBilling;
  },
  get getIsBillNotesEnabled() {
    return store.isBillNotesEnabled;
  },
  get getIsBillingOptionsEnabled() {
    return store.isBillingOptionsEnabled;
  }
}));
const getFeatureFlagsAsync = createAsync(() => {
  return async function getFeatureFlags(options) {
    const resp = await api.Account.getFeatureFlags(options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful) {
      getParent(this).account.featureFlagsStore.setFeatureFlags(resp.response);
    }
    return resp;
  };
});
const updatePasswordAsync = createAsync(body => {
  return async function updatePassword(options) {
    const resp = await api.Account.updatePassword(body, options);
    return resp;
  };
});
const validateEmailAsync = createAsync(body => {
  return async function validateEmail(options) {
    const resp = await api.Account.validateEmail(body, options);
    return resp;
  };
});
const AccountStore = types.model('AccountStore', {
  accountIdsValue: types.array(types.string),
  isConfirmed: false,
  isInviteValue: types.optional(types.boolean, false),
  isSavingCompany: types.optional(types.boolean, false),
  isSavingPaygate: types.optional(types.boolean, false),
  changeTemporaryInvitePasswordAsync,
  changeTemporaryInvitePasswordV2Async,
  changeTemporaryPasswordAsync,
  confirmAnAccountsEmailAsync,
  confirmPhoneNumberAsync,
  associateTotpAsync,
  disableAccountMfaAsync,
  enableAccountMfaAsync,
  enrollUserAsync,
  featureFlagsStore: types.optional(featureFlagsStore, {}),
  getFeatureFlagsAsync,
  forgotAccountPasswordAsync,
  getProfileAsync,
  getUsersProfilesAsync,
  loginAccountAsync,
  loginAccountV2Async,
  loginInviteAsync,
  logoutAccountAsync,
  inviteNewUserAsync,
  inviteNewUserV2Async,
  mfaFlowStore: types.optional(mfaFlowStore, {}),
  mfaLoginAccountAsync,
  profilesEmailValue: types.map(types.maybeNull(types.string)),
  profilesNewEmailValue: types.map(types.string),
  profilesFullNameValue: types.map(types.string),
  profilesSumIdValue: types.optional(types.string, ''),
  registerNewAccountAsync,
  verifyTotpAsync,
  refreshAccessTokenAsync,
  reInviteUserAsync,
  resendCodeToConfirmAccountEmailAsync,
  resetAccountPasswordAsync,
  updateEmailByAccountIdAsync,
  updatePasswordAsync,
  updatePhoneByAccountIdAsync,
  updatePhoneNumberAsync,
  updateProfileAsync,
  updateProfileByAccountIdAsync,
  updateUsersPortalAccessDateAsync,
  enableDemoPropertyAsync,
  setTeamMemberAccessAsync,
  assignResourceTeamMemberAsync,
  getUsersSubAccountsAsync,
  postSwitchUsersSubAccountAsync,
  validateEmailAsync,
  isEnablingDemoMode: types.optional(types.boolean, false),
  userProfile: types.optional(userProfile, userProfileInitialData)
}).actions(store => {
  return {
    setIsInvite(value) {
      try {
        store.isInviteValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setIsSavingCompany(value) {
      try {
        store.isSavingCompany = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setIsSavingPaygate(value) {
      try {
        store.isSavingPaygate = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setIsEnablingDemoMode(value) {
      try {
        store.isEnablingDemoMode = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setProfilesDataAfterFetch(data, accountIds) {
      try {
        store.accountIdsValue = cast([...accountIds, ...store.accountIdsValue]);
        data.response.forEach(item => {
          store.profilesFullNameValue.set(item.id, "".concat(item.firstName, " ").concat(item.lastName));
          if (item.email) store.profilesEmailValue.set(item.id, item.email);
          if (item.newEmail) store.profilesNewEmailValue.set(item.id, item.newEmail);
        });
        let profilesSumId = '';
        Array.from(store.profilesFullNameValue.entries()).forEach(i => {
          profilesSumId = profilesSumId + i[0];
        });
        store.profilesSumIdValue = profilesSumId;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setProfilesFullNamesAfterFetch(data) {
      try {
        data.response.buildingResidency.forEach(item => {
          store.profilesEmailValue.set(item.accountId, item.email);
        });
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    }
  };
}).views(store => ({
  get accountIds() {
    return toJS(store.accountIdsValue);
  },
  get isInvite() {
    return store.isInviteValue;
  },
  get profilesEmail() {
    return store.profilesEmailValue;
  },
  get profilesNewEmail() {
    return store.profilesNewEmailValue;
  },
  get profilesFullName() {
    return store.profilesFullNameValue;
  },
  get profilesSumId() {
    return store.profilesSumIdValue;
  },
  get isEnablingDemoModeState() {
    return store.isEnablingDemoMode;
  }
}));
export const account = types.optional(AccountStore, {});